import { useState, type MouseEvent, type SyntheticEvent } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslations } from '@/i18n/use-translations';
import { useDeleteEventMutation } from '@/state/mutations/events/use-delete-event.ts';
import { useCopyEventMutation } from '@/state/mutations/events/use-copy-event.ts';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';

interface EventRowActionsProps {
  event: EventDto;
}

function stopPortalEventPropagation(event: unknown) {
  // Menu/Modal portals still bubble React synthetic events to ancestors (the TableRow).
  if (event && typeof event === 'object' && 'stopPropagation' in event) {
    (event as SyntheticEvent).stopPropagation();
  }
}

export default function EventRowActions({ event }: EventRowActionsProps) {
  const t = useTranslations();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    type: 'copy' | 'delete';
  }>({
    open: false,
    type: 'copy',
  });

  const deleteEventMutation = useDeleteEventMutation();
  const copyEventMutation = useCopyEventMutation();
  const notification = useNotifications();

  const handleMenuOpen = (mouseEvent: MouseEvent<HTMLButtonElement>) => {
    mouseEvent.stopPropagation();
    setMenuAnchorEl(mouseEvent.currentTarget);
  };

  const handleMenuClose = (closeEvent?: unknown) => {
    if (closeEvent) {
      stopPortalEventPropagation(closeEvent);
    }
    setMenuAnchorEl(null);
  };

  // NOTE: we are temporarily disabling copy event functionality
  // const handleCopyEvent = (event: MouseEvent<HTMLLIElement>) => {
  //   event.stopPropagation();
  //   setConfirmationModal({
  //     open: true,
  //     type: 'copy',
  //   });
  //   handleMenuClose();
  // };

  const handleDeleteEvent = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setConfirmationModal({
      open: true,
      type: 'delete',
    });
    handleMenuClose();
  };

  const handleConfirmAction = async (mouseEvent: MouseEvent<HTMLButtonElement>) => {
    mouseEvent.stopPropagation();
    try {
      if (confirmationModal.type === 'copy') {
        await copyEventMutation.mutateAsync(event.id);
        notification.notifySuccess(t('event.notification.copySuccess', { title: event.title }));
      } else {
        await deleteEventMutation.mutateAsync(event.id);
        notification.notifySuccess(t('event.notification.deleteSuccess', { title: event.title }));
      }
      setConfirmationModal({ open: false, type: 'copy' });
    } catch (error) {
      console.error(`Failed to ${confirmationModal.type} event:`, error);
      notification.notifyError(
        confirmationModal.type === 'copy' ? t('event.notification.copyError') : t('event.notification.deleteError'),
      );
    }
  };

  const handleCancelAction = () => {
    setConfirmationModal({ open: false, type: 'copy' });
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        aria-controls={menuAnchorEl ? 'event-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuAnchorEl ? 'true' : undefined}
        sx={{
          'color': 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu
        id="event-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={stopPortalEventPropagation}
        slotProps={{
          list: {
            'aria-labelledby': 'event-menu-button',
          },
        }}
      >
        {/* NOTE: we are temporarily disabling copy event functionality */}
        {/* <MenuItem onClick={handleCopyEvent}> */}
        {/*   <ListItemIcon> */}
        {/*     <ContentCopyIcon fontSize="small" /> */}
        {/*   </ListItemIcon> */}
        {/*   <ListItemText>{t('common.duplicate')}</ListItemText> */}
        {/* </MenuItem> */}
        <MenuItem onClick={handleDeleteEvent}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmationModal
        open={confirmationModal.open}
        title={
          confirmationModal.type === 'copy' ? t('event.confirmation.copyTitle') : t('event.confirmation.deleteTitle')
        }
        text={
          confirmationModal.type === 'copy'
            ? t('event.confirmation.copyText', { title: event.title })
            : t('event.confirmation.deleteText', { title: event.title })
        }
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        isLoading={copyEventMutation.isPending || deleteEventMutation.isPending}
      />
    </>
  );
}
